/**
 * MapClusterSheet — pick a car from a map group or the complete result list.
 *
 * The map draws ONE pill per location. When that location holds more than one
 * car, the pill has to hand the user a way to reach every one of them; zooming
 * cannot, because co-located cars share a coordinate at every zoom level.
 *
 * Rows are `VehicleListItem` unchanged — the same photo, title, rating and
 * sticker price the explore list already draws — so this introduces no new
 * visual language, and the price comes from the one shared helper the map pins
 * and vehicle detail also use.
 *
 * Owned by MapPreview and imported by BOTH platform implementations, so the
 * full-screen map route, the desktop explore pane, iOS and Android all get the
 * same picker from one place.
 */
import React from 'react'
import { View, Text } from 'react-native'
import { ScrollView as RNScrollView } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { AppModal } from '../ui/AppModal'
import { GlassSurface } from '../ui/GlassSurface'
import { TrackedNativePressable as RNPressable } from '../ui/TrackedNativePressable'
import { X } from '../ui/icons'
import { VehicleListItem } from './VehicleListItem'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING, TEXT_STYLES } from '../../lib/theme/tokens'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { FONTS } from '../../lib/brand'
import type { NearbyVehicle } from '../../lib/types/vehicle.types'

interface MapClusterSheetProps {
  /** The group's cars, or all map results. An empty list closes the sheet. */
  vehicles: NearbyVehicle[]
  title?: string
  onSelect: (vehicleId: string) => void
  onClose: () => void
}

export function MapClusterSheet({ vehicles, title, onSelect, onClose }: MapClusterSheetProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  // A modal is its own window and really does reach the screen edge, so this is
  // the physical inset, not `useScreenInsets`.
  const insets = useSafeAreaInsets()

  const visible = vehicles.length > 0

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      testID="map.cluster-sheet-modal"
    >
      {/*
        EL SCRIM VA DETRAS, NO ALREDEDOR. Y esa es toda la diferencia.

        Antes el scrim y la tarjeta eran dos `Pressable` de react-native
        ANIDADOS, con las filas del carro adentro usando el nuestro
        (components/ui/Pressable), que en Android es el de gesture-handler. Dos
        sistemas de gestos distintos en el mismo arbol: el ancestro de RN reclama
        el responder en el ACTION_DOWN y gana el que no hace nada. Resultado
        medido: 1.751 toques de 267 personas sobre esta hoja -- 6,6 por persona
        en una lista donde solo hay que elegir uno.
        
        El comentario que estaba aca decia que un "hit test plano" evitaba una
        carrera de gestos contra las filas. Era exactamente al reves: la carrera
        la creaba el anidamiento, y la ganaba el de afuera.

        La forma correcta ya existia en components/ui/Select.tsx:229-253 -- el
        scrim absoluto DETRAS de la tarjeta, como hermano y no como ancestro. Sin
        anidar no hay a quien ganarle, y la tarjeta deja de necesitar un
        `onPress={() => {}}` para tragarse toques que ya no le llegan.

        El scrim no se anuncia como control a proposito: en web RN rinde un rol
        de boton como un <button> real, y las filas de adentro tambien son
        botones -- un boton dentro de otro es HTML invalido y un error de
        hidratacion. La tecnologia asistiva usa el boton de cerrar explicito.
      */}
      <View
        style={{
          flex: 1,
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          padding: isDesktop ? SPACING.xl : 0,
        }}
      >
        <RNPressable
          onPress={onClose}
          accessibilityRole="none"
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <GlassSurface
          testID="map.cluster-sheet"
          tone="regular"
          visualRole="sheet"
          radius={RADIUS.xl}
          style={{
            width: isDesktop ? 460 : undefined,
            maxWidth: isDesktop ? '100%' : undefined,
            maxHeight: isDesktop ? 640 : '72%',
            marginHorizontal: isDesktop ? 0 : SPACING.sm,
            marginBottom: isDesktop ? 0 : Math.max(insets.bottom, SPACING.sm),
            ...(isDesktop ? WEB_SHADOWS.xl : {}),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: SPACING.md,
              paddingHorizontal: SPACING.lg,
              paddingTop: SPACING.lg,
              paddingBottom: SPACING.md,
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  fontSize: TEXT_STYLES.h4.fontSize,
                  lineHeight: TEXT_STYLES.h4.lineHeight,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                }}
                numberOfLines={1}
              >
                {title ?? t('explore.map_cluster_sheet_title')}
              </Text>
              <Text
                style={{
                  fontSize: TEXT_STYLES.bodySm.fontSize,
                  lineHeight: TEXT_STYLES.bodySm.lineHeight,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('explore.map_cluster_count', { count: vehicles.length })}
              </Text>
            </View>
            <RNPressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              testID="map.cluster-sheet-close"
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GlassSurface
                tone="quiet"
                radius={RADIUS.full}
                style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} color={colors.textPrimary} strokeWidth={2} />
              </GlassSurface>
            </RNPressable>
          </View>

          {/*
            `flexShrink: 1` es lo unico que separa esta hoja de estar rota.

            Sin el, el ScrollView se dimensiona al alto de SU CONTENIDO: cree
            que entra entero y por lo tanto que no hay nada que desplazar. El
            contenedor de arriba tiene `maxHeight: '72%'` y `overflow: hidden`,
            asi que lo que sobra no se corta con un scroll -- se corta y ya. Con
            16 carros en un punto, la persona ve siete y los otros nueve no
            existen.

            `flexShrink: 1` y NO `flex: 1` a proposito: con dos carros la hoja
            tiene que abrazar su contenido, no estirarse al 72% de la pantalla
            dejando un vacio abajo. Encoge solo cuando choca contra el tope.
          */}
          <RNScrollView
            style={{ flexShrink: 1 }}
            contentContainerStyle={{
              padding: SPACING.md,
              gap: SPACING.sm,
              paddingBottom: SPACING.md + (isDesktop ? 0 : insets.bottom),
            }}
            showsVerticalScrollIndicator={false}
          >
            {vehicles.map((vehicle) => (
              <VehicleListItem
                key={vehicle.id}
                vehicle={vehicle}
                desktop={isDesktop}
                onPress={() => onSelect(vehicle.id)}
              />
            ))}
          </RNScrollView>
        </GlassSurface>
      </View>
    </AppModal>
  )
}
